/**
 * Configuration for page view emission
 */

/**
 * Window during which a repeat of the page view we just sent, on the same URL,
 * is dropped. `page()` is called on every history entry change, so a single
 * navigation that rewrites its URL more than once would otherwise bill one
 * event per rewrite.
 */
export const PAGE_VIEW_DEDUP_WINDOW_MS = 2000;

/**
 * Trailing debounce applied to navigation-triggered page views. URL state
 * (filters, sort, pagination, search) lands as a burst of successive
 * `replaceState` calls; we report the URL as it stands once the burst settles.
 *
 * This defers the page view only. Navigation itself stays synchronous, so the
 * app and organization a `track()` is stamped with are never stale.
 */
export const NAVIGATION_DEBOUNCE_MS = 300;

/**
 * Ceiling on the trailing debounce above. A burst that never settles — a search
 * box writing history on every keystroke — would otherwise restart the timer
 * indefinitely and report nothing at all. Past this delay the pending page view
 * is sent with the URL as it stands.
 */
export const MAX_NAVIGATION_DEFER_MS = 1000;

/**
 * Query parameters ignored when deciding whether two page views describe the
 * same page.
 *
 * These carry a one-shot authentication credential, not page state: an OAuth
 * callback lands on the app's redirect URL with a fresh `code` and `state` every
 * time, and the app then rewrites the URL to clean them off. Compared raw, the
 * callback URL and the cleaned URL look like two different pages, and a client
 * that re-authorizes in a loop reports two page views per cycle forever.
 *
 * Everything else is kept: filters, sort, pagination and search are real page
 * state and must still distinguish one page view from the next.
 */
export const PAGE_VIEW_IGNORED_QUERY_PARAMS = [
	'code',
	'error',
	'error_description',
	'iss',
	'session_state',
	'state',
];
